/**
 * Business Office page — orchestrates comment display, save/trigger-RPA
 * actions, modal lifecycle, and error handling for a single medical PA order.
 * @module BusniessOffice
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useBusniessOfficeNotesContext } from "../../../../context/busniessOfficeNotesContext";
import { useDataAccumulatorForSingleOrder } from "../../../../context/dataAccumulatorForSingleOrder";
import { usePayerNameContextFromSingleOrderLayout } from "../../../../context/payerNameContextFromSingleOrderLayout";
import { useRemoteConfig } from "../../../../context/remoteConfigContext";
import { User } from "../../../../data-model/firebaseAuthModel";
import {
  AuthOnFile,
  GeneralOrderDetails,
} from "../../../../data-model/medicalPaOrdersModel";
import { AuthOnFileRpaErrorType } from "../../../../enums/authOnFileRpaErrorType";
import { DropdownLocation } from "../../../../enums/dropdownLocation";
import { ModalId } from "../../../../enums/modalId";
import { isFcHoldOrg } from "../../../../enums/organizationTypes";
import { useAuthOnFileError } from "../../../../hooks/useAuthOnFileError";
import {
  closeModal,
  setOpenedModalId,
} from "../../../../redux/slice/modalSlice";
import { AppDispatch, RootState } from "../../../../redux/store/store";
import { getOrgIdForFetchExternalWorklist } from "../../../../utils/organizationHelper";
import { getValidateTemplatePayloadFromBoNotes } from "../../../../utils/postCommentHelper";
import StatusError from "../errorComponents/statusError";
import { handleSaveBoNotes } from "./actionHandlers/handleSaveBoNotes";
import { handleTriggerRpa } from "./actionHandlers/handleTriggerRpa";
import AllComments from "./components/allComments";
import BoModals from "./components/BoModals";
import BusniessOfficeNotesHeader from "./components/busniessOfficeNotesHeader";
import { useBoActionState } from "./hooks/useBoActionState";
import { useBoCommentState } from "./hooks/useBoCommentState";
import { useBoModalEffects } from "./hooks/useBoModalEffects";

/**
 * Renders the Business Office experience for a single order: sorted comments,
 * save / trigger-RPA header, and gated modals driven by Redux `openedModalId`.
 *
 * @returns BO content or an auth-on-file error placeholder when BO rows are missing
 */
const BusniessOffice = () => {
  // const { singleMedicalPaOrder } = useSelector(
  //   (state: RootState) => state.singleMedicalPaOrder,
  // );
  const abortController = useRef<AbortController | null>(null);
  const {
    generalOrderDetails,
    authOnFileDetails,
    fetchAll,
    filteredSiblingOrders,
  } = useDataAccumulatorForSingleOrder();
  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );
  const { data: providerDetails } = useSelector(
    (state: RootState) => state.providerDetails,
  );
  const { data: boOptions } = useSelector(
    (state: RootState) => state.boOptions,
  );
  const { data: singleHealthFacilityDetails } = useSelector(
    (state: RootState) => state.singleHealthFacilityDetails,
  );
  const { openedModalId } = useSelector((state: RootState) => state.modal);
  const {
    setAllCommentsForUI,
    allCommentsForUI,
    hasCptTableAnyError,
    shouldDisableMarkAsComplete,
    globalBoValue,
  } = useBusniessOfficeNotesContext();
  const { payerName } = usePayerNameContextFromSingleOrderLayout();
  const { config: remoteConfig } = useRemoteConfig();
  const dispatch = useDispatch<AppDispatch>();

  const commentState = useBoCommentState({
    allCommentsForUI,
    setAllCommentsForUI,
    orderId: generalOrderDetails?.order?.order_id ?? "",
  });

  const actingProviderName =
    `${providerDetails?.FirstName || ""} ${providerDetails?.LastName || ""}`.trim() ||
    "Unknown Provider";

  const [fcHoldCommentDraft, setFcHoldCommentDraft] = useState<
    string | undefined
  >(undefined);

  const actionState = useBoActionState({
    user,
    payerName,
    providerName: actingProviderName,
    allCommentsForUI,
    hasCptTableAnyError,
    boOptions,
    fcHoldCommentDraft,
    previousBoStatus: generalOrderDetails?.order?.status?.bo_status,
    dispatch,
    fetchAll: () => fetchAll(generalOrderDetails?.order?.order_id ?? "", orgId),
  });

  useBoModalEffects(openedModalId);

  const orgId = getOrgIdForFetchExternalWorklist() ?? "";
  const orderId = generalOrderDetails?.order?.order_id ?? "";
  const authStatus = authOnFileDetails?.auth_on_file?.auth_status ?? "";
  const fcHoldComment = isFcHoldOrg(orgId)
    ? generalOrderDetails?.order?.status?.fc_hold_comment
    : undefined;

  // A stale edit from a previous order must not be saved to the next one.
  useEffect(() => {
    setFcHoldCommentDraft(undefined);
  }, [orderId]);
  const commentsWritebackValidationPayload = useMemo(
    () =>
      getValidateTemplatePayloadFromBoNotes(
        allCommentsForUI,
        orgId,
        orderId,
        authStatus,
      ),
    [allCommentsForUI, orgId, orderId, authStatus],
  );

  const onProceedToTriggerRpa = useCallback(() => {
    dispatch(closeModal());
    dispatch(setOpenedModalId({ id: ModalId.TRIGGER_RPA_MODAL_NEW }));
  }, [dispatch]);

  const {
    shouldShowError: shouldShowBusinessOfficeRowsNotFound,
    errorMessage,
    dropdownLocation,
  } = useAuthOnFileError(AuthOnFileRpaErrorType.BUSINESS_OFFICE_ROWS_NOT_FOUND);

  useEffect(() => {
    return () => {
      abortController.current?.abort();
    };
  }, []);

  const shouldShowDropdownInBo =
    dropdownLocation === DropdownLocation.BUSINESS_OFFICE ||
    dropdownLocation === DropdownLocation.BOTH;

  if (shouldShowBusinessOfficeRowsNotFound) {
    return (
      <div className="busniess-office-container h-full">
        <StatusError
          title={errorMessage}
          showDropdown={shouldShowDropdownInBo}
        />
      </div>
    );
  }

  return (
    <div className="busniess-office-container">
      {!actionState.shouldHideTriggerRpaButton &&
        !shouldDisableMarkAsComplete && (
          <BusniessOfficeNotesHeader
            orderId={generalOrderDetails?.order?.order_id ?? ""}
            rpaBlockers={actionState.rpaBlockers}
            saveBoLoading={actionState.saveBoLoading}
            handleSaveBoNotes={() =>
              handleSaveBoNotes(
                generalOrderDetails?.order ?? ({} as GeneralOrderDetails),
                authOnFileDetails?.auth_on_file ?? ({} as AuthOnFile),
                user ?? ({ id: "", email: "" } as User),
                payerName ?? "",
                globalBoValue ?? "",
                allCommentsForUI ?? [],
                actingProviderName,
                actionState.setSaveBoLoading,
                abortController,
                () => fetchAll(orderId, orgId),
                filteredSiblingOrders?.siblings?.map(
                  (sibling) => sibling.order_id,
                ) ?? [],
              )
            }
            handleTriggerRpa={() =>
              handleTriggerRpa(
                generalOrderDetails?.order ?? ({} as GeneralOrderDetails),
                user ?? ({ id: "", email: "" } as User),
                payerName ?? "",
                dispatch,
                remoteConfig.notes_qc_version_1_rollout,
                authOnFileDetails?.auth_on_file?.auth_status,
                singleHealthFacilityDetails?.feature_config,
              )
            }
            fetchAll={() => fetchAll(orderId, orgId)}
            siblingOrderIds={
              filteredSiblingOrders?.siblings?.map(
                (sibling) => sibling.order_id,
              ) ?? []
            }
          />
        )}

      {commentState.sortedCommentsForUI.map((comment, groupIndex) => (
        <AllComments
          key={comment.commentDate}
          {...comment}
          fcHoldComment={groupIndex === 0 ? fcHoldComment : undefined}
          onFcHoldCommentChange={
            groupIndex === 0 ? setFcHoldCommentDraft : undefined
          }
          onBoStatusChange={commentState.handleBoStatusChange}
          onWriteBackCommentChange={commentState.handleWriteBackCommentChange}
          generalOrderDetails={
            generalOrderDetails?.order ?? ({} as GeneralOrderDetails)
          }
          authOnFile={authOnFileDetails?.auth_on_file ?? ({} as AuthOnFile)}
        />
      ))}

      <BoModals
        openedModalId={openedModalId}
        commentsWritebackValidationPayload={commentsWritebackValidationPayload}
        allCommentsForUI={allCommentsForUI}
        onProceedToTriggerRpa={onProceedToTriggerRpa}
        onTriggerRpa={actionState.handleSaveChanges}
        onClose={() => dispatch(closeModal())}
        loading={actionState.loading}
      />
    </div>
  );
};

export default BusniessOffice;
